/**
 * Real-time updates hook using Server-Sent Events (SSE)
 * Connects to the observatory-api /events/stream endpoint
 * and invalidates React Query caches when relevant events occur
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { EventSourcePolyfill } from 'event-source-polyfill'
import { useAutomationClient } from '../providers/AutomationProvider'

// Event types that match the backend DomainEvent
export type RealtimeEventType =
  // Errors
  | 'error:synced'
  | 'error:updated'
  // Runs
  | 'run:completed'
  | 'run:failed'
  // Tests
  | 'test:completed'
  | 'test:suite:completed'
  // Stats
  | 'stats:invalidated'
  | 'stats:updated'
  // Alerts
  | 'alert:fired'
  | 'alert:resolved'
  | 'alert:acknowledged'
  // Tickets
  | 'ticket:created'
  | 'ticket:updated'
  | 'ticket:resolved'
  // Linear
  | 'linear:issue:created'
  | 'linear:issue:updated'
  // Automations
  | 'automation:status:changed'
  | 'automation:error:detected'

export interface RealtimeEvent {
  type: RealtimeEventType
  payload: Record<string, unknown>
  timestamp: string
}

export type RealtimeConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error'

export interface UseRealtimeOptions {
  /** Enable/disable the SSE connection */
  enabled?: boolean
  /** Custom event handler for all events */
  onEvent?: (event: RealtimeEvent) => void
  /** Handler for specific event types */
  onEventType?: Partial<Record<RealtimeEventType, (payload: Record<string, unknown>) => void>>
  /** Auto-invalidate React Query caches (default: true) */
  autoInvalidate?: boolean
  /** Reconnect delay in ms (default: 3000) */
  reconnectDelay?: number
  /** Max reconnect attempts (default: 10) */
  maxReconnectAttempts?: number
}

export interface UseRealtimeResult {
  /** Current connection status */
  status: RealtimeConnectionStatus
  /** Last received event */
  lastEvent: RealtimeEvent | null
  /** Whether Redis is available on the server */
  redisAvailable: boolean
  /** Manually reconnect */
  reconnect: () => void
  /** Manually disconnect */
  disconnect: () => void
}

/**
 * Hook to connect to real-time events via SSE
 * Automatically invalidates React Query caches when relevant events occur
 */
export function useRealtime(options: UseRealtimeOptions = {}): UseRealtimeResult {
  const {
    enabled = true,
    onEvent,
    onEventType,
    autoInvalidate = true,
    reconnectDelay = 3000,
    maxReconnectAttempts = 10,
  } = options

  const client = useAutomationClient()
  const queryClient = useQueryClient()

  const [status, setStatus] = useState<RealtimeConnectionStatus>('disconnected')
  const [lastEvent, setLastEvent] = useState<RealtimeEvent | null>(null)
  const [redisAvailable, setRedisAvailable] = useState(false)

  const eventSourceRef = useRef<EventSourcePolyfill | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Map event types to query keys to invalidate
  const getQueryKeysToInvalidate = useCallback((eventType: RealtimeEventType): string[][] => {
    switch (eventType) {
      case 'error:synced':
      case 'error:updated':
        return [['errors'], ['stats']]

      case 'run:completed':
      case 'run:failed':
        return [['automations'], ['stats'], ['traces']]

      case 'test:completed':
        return [['test-cases'], ['stats']]

      case 'test:suite:completed':
        return [['test-suites'], ['stats']]

      case 'stats:invalidated':
      case 'stats:updated':
        return [['stats']]

      case 'alert:fired':
      case 'alert:resolved':
      case 'alert:acknowledged':
        return [['alerts'], ['stats']]

      case 'ticket:created':
      case 'ticket:updated':
      case 'ticket:resolved':
        return [['tickets']]

      case 'linear:issue:created':
      case 'linear:issue:updated':
        return [['linear'], ['errors']]

      case 'automation:status:changed':
      case 'automation:error:detected':
        return [['automations'], ['stats']]

      default:
        return []
    }
  }, [])

  const handleEvent = useCallback((event: RealtimeEvent) => {
    setLastEvent(event)

    // Call custom event handler
    onEvent?.(event)

    // Call type-specific handler
    const typeHandler = onEventType?.[event.type]
    if (typeHandler) {
      typeHandler(event.payload)
    }

    // Auto-invalidate React Query caches
    if (autoInvalidate) {
      const queryKeys = getQueryKeysToInvalidate(event.type)
      for (const key of queryKeys) {
        queryClient.invalidateQueries({ queryKey: key })
      }
    }
  }, [onEvent, onEventType, autoInvalidate, queryClient, getQueryKeysToInvalidate])

  const connect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }

    const baseUrl = client.baseUrl || ''
    const url = `${baseUrl}/api/events/stream`

    setStatus('connecting')

    try {
      // Use EventSourcePolyfill to support credentials for cross-origin requests
      const eventSource = new EventSourcePolyfill(url, {
        withCredentials: true,
      })
      eventSourceRef.current = eventSource

      eventSource.onopen = () => {
        setStatus('connected')
        reconnectAttemptsRef.current = 0
      }

      // Handle connection event
      eventSource.addEventListener('connected', (e) => {
        const data = JSON.parse((e as MessageEvent).data)
        setRedisAvailable(data.redisAvailable)
      })

      // Handle warning event
      eventSource.addEventListener('warning', (e) => {
        const data = JSON.parse((e as MessageEvent).data)
        console.warn('[Realtime] Warning:', data.message)
      })

      // Handle all domain events
      const eventTypes: RealtimeEventType[] = [
        'error:synced', 'error:updated',
        'run:completed', 'run:failed',
        'test:completed', 'test:suite:completed',
        'stats:invalidated', 'stats:updated',
        'alert:fired', 'alert:resolved', 'alert:acknowledged',
        'ticket:created', 'ticket:updated', 'ticket:resolved',
        'linear:issue:created', 'linear:issue:updated',
        'automation:status:changed', 'automation:error:detected',
      ]

      for (const eventType of eventTypes) {
        eventSource.addEventListener(eventType, (e) => {
          try {
            const data = JSON.parse((e as MessageEvent).data)
            handleEvent({
              type: eventType,
              payload: data.payload,
              timestamp: data.timestamp,
            })
          } catch (error) {
            console.error('[Realtime] Failed to parse event:', error)
          }
        })
      }

      eventSource.onerror = () => {
        setStatus('error')
        eventSource.close()

        // Attempt reconnection
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++
          console.log(`[Realtime] Reconnecting... (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`)

          reconnectTimeoutRef.current = setTimeout(() => {
            connect()
          }, reconnectDelay)
        } else {
          console.error('[Realtime] Max reconnect attempts reached')
          setStatus('disconnected')
        }
      }
    } catch (error) {
      console.error('[Realtime] Failed to connect:', error)
      setStatus('error')
    }
  }, [client.baseUrl, handleEvent, maxReconnectAttempts, reconnectDelay])

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }

    setStatus('disconnected')
    reconnectAttemptsRef.current = 0
  }, [])

  const reconnect = useCallback(() => {
    disconnect()
    reconnectAttemptsRef.current = 0
    connect()
  }, [connect, disconnect])

  // Connect/disconnect based on enabled prop
  useEffect(() => {
    if (enabled) {
      connect()
    } else {
      disconnect()
    }

    return () => {
      disconnect()
    }
  }, [enabled, connect, disconnect])

  return {
    status,
    lastEvent,
    redisAvailable,
    reconnect,
    disconnect,
  }
}

/**
 * Hook to check if real-time events are available
 */
export function useRealtimeStatus() {
  const client = useAutomationClient()
  const [status, setStatus] = useState<{
    enabled: boolean
    redisAvailable: boolean
    connectedClients: number
  } | null>(null)

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await fetch(`${client.baseUrl}/api/events/status`)
        if (response.ok) {
          const data = await response.json()
          setStatus(data)
        }
      } catch {
        setStatus(null)
      }
    }

    checkStatus()
    const interval = setInterval(checkStatus, 30000) // Check every 30 seconds

    return () => clearInterval(interval)
  }, [client.baseUrl])

  return status
}

// Export query keys for manual invalidation
export const realtimeKeys = {
  status: ['realtime', 'status'] as const,
}
